import React, { useEffect, useState } from "react";

const ActivityTrackingGuide: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6 md:px-12 fixed w-full z-[1000] top-0">
        <nav className="container mx-auto flex justify-between items-center">
          <a
            href="/"
            className="text-2xl font-bold text-brand-dark-blue rounded-md p-2 hover:bg-gray-100 transition-colors"
          >
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10"
            />
          </a>
          <div className="hidden md:flex space-x-6">
            <a
              href="/"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
            >
              Home
            </a>
            <a
              href="/login"
              className="text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md px-4 py-2 ml-4 shadow-md hover:scale-105 duration-200"
            >
              Log In
            </a>
          </div>
          <button
            className="md:hidden text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-dark-blue rounded-md p-2"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </nav>
        <div
          className={`md:hidden bg-white py-2 mt-2 border-t border-gray-200${
            mobileMenuOpen ? "" : " hidden"
          }`}
        >
          <a
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
          >
            Home
          </a>
          <a
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md mx-2 mt-2 shadow-md text-center"
          >
            Log In
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <section className="bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white py-20 px-6 md:px-12 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Activity Tracking Guide
            </h1>
            <p className="text-xl opacity-90">
              The Habit-Building Guide for Successful Patching
            </p>
          </div>
        </section>

        <section className="py-16 px-6 md:px-12 bg-white">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              {/* Why Tracking Matters */}
              <div className="bg-gray-50 p-6 mb-8">
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  📍 Why Tracking Matters for Amblyopia Treatment
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Amblyopia improvement relies on consistency, not intensity.
                  Progress is often gradual and invisible day-to-day — which
                  can make it easy to lose motivation.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                  Tracking helps:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Build daily routine stability</li>
                  <li>Create visual proof of progress</li>
                  <li>Turn patching into a non-negotiable habit</li>
                  <li>
                    Reduce parent–child resistance by turning it into a goal, not
                    a chore
                  </li>
                  <li>Improve outcomes because missed days matter</li>
                </ul>
                <p className="text-gray-700 leading-relaxed font-semibold text-brand-dark-blue">
                  ➡ Amblyfy aims to make treatment measurable, rewarding, and
                  empowering.
                </p>
              </div>

              {/* Step 1 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 1 — Define the Patching Habit
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg mb-4">
                  <h3 className="text-lg font-semibold text-brand-cyan mb-4">
                    Make the habit simple and predictable
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The most successful families anchor patching to a daily
                    ritual:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-brand-dark-blue text-white">
                          <th className="border border-gray-300 px-4 py-3 text-left">
                            Anchor Example
                          </th>
                          <th className="border border-gray-300 px-4 py-3 text-left">
                            Cue
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3">
                            After breakfast
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Glass goes on → patch goes on
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3">
                            During screen time
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            TV = Patch Time
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3">
                            Homework hour
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Desk = Patch time
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3">
                            Evening reading
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Book = Patch time
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
                    Consistency &gt; perfection
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2 text-brand-dark-blue">
                    ➡ Amblyfy can assist by letting users set anchor-based
                    reminders.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 2 — Track Behavior, Not Just Time
                </h2>
                <div className="bg-gray-50 p-6 mb-4">
                  <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                    Amblyfy's tracking helps reinforce the right psychology:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    <li>✔ Timer for active tracking</li>
                    <li>✔ Checkmark for completion</li>
                    <li>✔ Streak visual for momentum</li>
                    <li>✔ Calendar for overview</li>
                    <li>✔ Parent notes for behavior &amp; comfort</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-brand-dark-blue mb-3">
                    What the user should track:
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Was the patch worn today?</li>
                    <li>For how long?</li>
                    <li>Was the session calm, resistant, or interrupted?</li>
                    <li>Any motivators/rewards that helped?</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-4 font-semibold">
                    Tracking isn't only about compliance — it's insight.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 3 — Use the Reward Loop (Crucial for Kids)
                </h2>
                <div className="bg-gray-50 p-6 mb-4">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A successful patching routine follows this loop:
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4 font-semibold text-lg">
                    Cue → Patch On → Track Time → Positive Reward
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-3">
                      Rewards can be:
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>🌟 Stickers</li>
                      <li>📱 15 min screen time</li>
                      <li>🎮 Game token</li>
                      <li>🎧 Favorite music/podcast</li>
                      <li>🧩 Puzzle time</li>
                      <li>🥇 Unlockable "Amblyfy achievements"</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-3">
                      Amblyfy reinforces this with:
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>Digital rewards</li>
                      <li>Streak celebration</li>
                      <li>Monthly completion badges</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg ">
                  <p className="text-gray-700 leading-relaxed font-semibold">
                    ➡ The brain loves winning → So we turn patching into
                    something to win, not endure.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 4 — Build Momentum With Streaks &amp; Calendars
                </h2>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                      Streaks work because:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                      <li>
                        Breaking a streak produces a stronger emotional reaction
                        than starting one
                      </li>
                      <li>
                        Children visualize success better than conceptualize it
                      </li>
                      <li>
                        Parents feel successful when the calendar shows progress
                      </li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed font-semibold text-brand-dark-blue">
                      Goal: "Don't break the chain."
                    </p>
                  </div>
                  <div className="bg-gray-50 p-6">
                    <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                      But if you miss a day:
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-2 font-bold text-lg">
                      ❗ Rule: Never miss two days in a row.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      Amblyfy can send an encouraging message:
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-2 italic">
                      "Yesterday was missed, but today can reset the streak 💪"
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 5 — Review Progress Weekly (Short &amp; Simple)
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg mb-4">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    A 2-minute reflection:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-brand-dark-blue text-white">
                          <th className="border border-gray-300 px-4 py-3 text-left">
                            Question
                          </th>
                          <th className="border border-gray-300 px-4 py-3 text-left">
                            Insight
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3">
                            What time of day worked best?
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Optimize schedule
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3">
                            Were there struggles?
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Adjust duration
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-4 py-3">
                            Was the reward motivating?
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Experiment
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-4 py-3">
                            Is the streak growing?
                          </td>
                          <td className="border border-gray-300 px-4 py-3">
                            Celebrate
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Amblyfy can evolve into a parent–therapist communication
                    tool:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mt-2">
                    <li>Share notes</li>
                    <li>Share streak reports</li>
                    <li>Track dosage changes</li>
                  </ul>
                </div>
              </div>

              {/* Step 6 */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Step 6 — Scale the Habit Slowly
                </h2>
                <div className="bg-gray-50 p-6 mb-4">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Start with success-friendly duration
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Then increase gradually (per doctor's plan).
                  </p>
                  <div className="bg-white p-4 rounded-lg mb-4">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-3">
                      Example:
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      <li>Week 1: 45 min/day</li>
                      <li>Week 2–3: 60 min/day</li>
                      <li>Week 4: 90 min/day</li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-relaxed font-semibold">
                    📌 Never increase faster than the child's emotional
                    tolerance.
                  </p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-3 font-semibold">
                    Amblyfy can support:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Progressive schedule settings</li>
                    <li>Graphs that show improvement over time</li>
                    <li>Alerts when progress milestones are reached</li>
                  </ul>
                </div>
              </div>

              {/* Pitfalls */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Pitfalls (and How Amblyfy Prevents Them)
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-brand-dark-blue text-white">
                        <th className="border border-gray-300 px-4 py-3 text-left">
                          Common Issue
                        </th>
                        <th className="border border-gray-300 px-4 py-3 text-left">
                          What Helps
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3">
                          Forgetfulness
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          Reminders &amp; anchors
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3">
                          Child resistance
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          Rewards &amp; choice ("Which eye patch today?")
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3">
                          Missed days
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          Streak recovery system
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3">
                          Feeling like "no progress"
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          Calendar &amp; progress visuals
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-3">
                          Lack of clarity
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          Notes &amp; doctor summary
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg mt-4 ">
                  <p className="text-gray-700 leading-relaxed font-semibold text-lg">
                    🎯 Amblyfy Rule:
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    Motivation is unpredictable — systems are reliable.
                  </p>
                </div>
              </div>

              {/* Closing Message */}
              <div className="bg-gray-50 p-8 rounded-lg mt-8">
                <p className="text-lg leading-relaxed text-gray-800 mb-4">
                  Amblyfy isn't just about tracking time — it's about
                  empowering families to build healthy vision habits that last.
                </p>
                <p className="text-lg leading-relaxed text-gray-800 mb-4">
                  We take daily challenges and turn them into measurable
                  victories.
                </p>
                <p className="text-lg leading-relaxed text-gray-800 font-semibold">
                  Every minute tracked becomes a step toward stronger vision and
                  long-term confidence.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 w-full">
        <div className="w-full px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-4">
            <p className="text-gray-600">
              &copy; 2025 Amblyfy. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/privacy-policy"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/evidence-statement"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Evidence Statement
              </a>
              <a
                href="/activity-tracking-guide"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Activity Tracking Guide
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={handleBackToTop}
          className="fixed bottom-8 right-8 bg-brand-dark-blue text-white p-4 rounded-full shadow-lg hover:bg-brand-cyan transition-all duration-300 z-50 hover:scale-110"
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </>
  );
};

export default ActivityTrackingGuide;

